/**
 * RevenueFormModal
 * 매출 추가/수정 모달
 */

import React, { useState, useEffect } from 'react';
import { TaxRevenue, CreateRevenueRequest } from '../../models/Tax';
import { getTodayKST } from '../../../../utils/dateUtils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateRevenueRequest) => Promise<boolean>;
  editItem?: TaxRevenue | null;
}

export const RevenueFormModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  editItem,
}) => {
  const [form, setForm] = useState({
    date: '',
    client: '',
    description: '',
    amount: '',
    vat: '',
    total: '',
    invoice_issued: false,
    memo: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editItem) {
      setForm({
        date: editItem.date,
        client: editItem.client,
        description: editItem.description || '',
        amount: String(editItem.amount),
        vat: String(editItem.vat),
        total: String(editItem.total),
        invoice_issued: editItem.invoice_issued,
        memo: editItem.memo || '',
      });
    } else {
      setForm({
        date: getTodayKST(),
        client: '',
        description: '',
        amount: '',
        vat: '',
        total: '',
        invoice_issued: false,
        memo: '',
      });
    }
  }, [editItem, isOpen]);

  const handleAmountChange = (field: 'amount' | 'vat' | 'total', value: string) => {
    const numVal = value.replace(/[^0-9-]/g, '');
    const newForm = { ...form, [field]: numVal };

    if (field === 'total' && numVal) {
      const totalVal = parseInt(numVal);
      const vatVal = Math.round(totalVal / 11);
      newForm.vat = String(vatVal);
      newForm.amount = String(totalVal - vatVal);
    } else if (field === 'amount' && numVal && form.vat) {
      newForm.total = String(parseInt(numVal) + parseInt(form.vat || '0'));
    } else if (field === 'vat' && numVal && form.amount) {
      newForm.total = String(parseInt(form.amount || '0') + parseInt(numVal));
    }

    setForm(newForm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.date || !form.client || !form.total) return;

    setSubmitting(true);
    const success = await onSubmit({
      date: form.date,
      client: form.client,
      description: form.description || undefined,
      amount: parseInt(form.amount) || 0,
      vat: parseInt(form.vat) || 0,
      total: parseInt(form.total) || 0,
      invoice_issued: form.invoice_issued,
      memo: form.memo || undefined,
    });
    setSubmitting(false);
    if (success) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {editItem ? '매출 수정' : '매출 추가'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">날짜 *</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">거래처 *</label>
            <input
              type="text"
              value={form.client}
              onChange={(e) => setForm({ ...form, client: e.target.value })}
              placeholder="예: 이루는자리 독서실"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">내용</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="예: 3월 클라우드 서비스 이용료"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">합계 *</label>
              <input
                type="text"
                value={form.total}
                onChange={(e) => handleAmountChange('total', e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">공급가액</label>
              <input
                type="text"
                value={form.amount}
                onChange={(e) => handleAmountChange('amount', e.target.value)}
                placeholder="자동계산"
                className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">부가세</label>
              <input
                type="text"
                value={form.vat}
                onChange={(e) => handleAmountChange('vat', e.target.value)}
                placeholder="자동계산"
                className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50"
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="invoice_issued"
              checked={form.invoice_issued}
              onChange={(e) => setForm({ ...form, invoice_issued: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 mr-2"
            />
            <label htmlFor="invoice_issued" className="text-sm text-gray-700">
              세금계산서 발행 완료
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
            <textarea
              value={form.memo}
              onChange={(e) => setForm({ ...form, memo: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
              취소
            </button>
            <button type="submit" disabled={submitting} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              {submitting ? '저장 중...' : editItem ? '수정' : '추가'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
